import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useApp } from '@/hooks/use-app-context';
import { Icon } from '@/lib/icons';
import { resolveNavRole, type NavRole } from '@/components/layout/header/navigation-registry';
import {
  FIRST_LOGIN_WELCOME_EVENT,
  clearFirstLoginWelcome,
  displayNameFor,
  isWelcomeDeferredOn,
  pendingFirstLoginWelcome,
} from '@/lib/login-welcome';
import { pendingProfileFields } from '@/features/profile-completion/profile-completion';

/** Where each role does its main work. */
const HOME_BY_ROLE: Record<NavRole, string> = {
  guest: '/speak-contribute',
  user: '/speak-contribute',
  collector: '/collector',
  moderator: '/moderator',
  admin: '/admin/dashboard',
  accountant: '/accountant/dashboard',
};

export default function FirstLoginWelcomeDialog() {
  const { user, t } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingFor, setPendingFor] = useState<string | null>(() => pendingFirstLoginWelcome());

  useEffect(() => {
    const refresh = () => setPendingFor(pendingFirstLoginWelcome());
    window.addEventListener(FIRST_LOGIN_WELCOME_EVENT, refresh);
    return () => window.removeEventListener(FIRST_LOGIN_WELCOME_EVENT, refresh);
  }, []);

  const open =
    Boolean(user?.id) &&
    pendingFor === String(user?.id) &&
    !user?.mustChangePassword &&
    // The mandatory profile step comes first; the welcome waits behind it.
    !pendingProfileFields(user)?.length &&
    !isWelcomeDeferredOn(location.pathname);

  const dismiss = () => {
    clearFirstLoginWelcome();
    setPendingFor(null);
  };

  const role = resolveNavRole(user);
  const isContributor = role === 'user' || role === 'guest';
  const lang = location.pathname.split('/')[1] === 'en' ? 'en' : 'fr';

  const start = () => {
    dismiss();
    navigate(`/${lang}${HOME_BY_ROLE[role]}`);
  };

  if (!open) return null;

  return (
    <Dialog open onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-5 rounded-2xl p-6 sm:rounded-2xl">
        <DialogHeader className="space-y-2 text-left">
          <DialogTitle className="pr-6 text-lg font-semibold leading-snug tracking-tight">
            {t.firstLoginWelcomeTitle.replace('{name}', displayNameFor(user))}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6">
            {t.firstLoginWelcomeIntro}
          </DialogDescription>
        </DialogHeader>

        {isContributor && (
          <ul className="space-y-3 text-sm leading-6 text-muted-foreground">
            <li className="flex gap-3">
              <Icon name="mic" className="mt-1 h-4 w-4 shrink-0 text-primary" strokeWidth={1.4} />
              <span>{t.firstLoginWelcomeStepTask}</span>
            </li>
            <li className="flex gap-3">
              <Icon name="barChart3" className="mt-1 h-4 w-4 shrink-0 text-primary" strokeWidth={1.4} />
              <span>{t.firstLoginWelcomeStepProgress}</span>
            </li>
          </ul>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={dismiss} className="w-full sm:w-auto">
            {t.firstLoginWelcomeLater}
          </Button>
          <Button onClick={start} className="w-full sm:w-auto">
            {isContributor ? t.firstLoginWelcomeCta : t.firstLoginWelcomeCtaWorkspace}
            <Icon name="arrowRight" className="ml-1.5 h-4 w-4" strokeWidth={1.4} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
